import {
  For,
  Index,
  Show,
  createMemo,
  createResource,
  createSignal,
  type Component,
} from "solid-js";
import { ApiError, api } from "../api";
import type { EventType, Session, User } from "../types";
import Brand from "./Brand";

type Tab = "events" | "availability" | "meetings" | "profile" | "admin";

interface AvailabilityRange {
  start_time: string;
  end_time: string;
}

interface AvailabilityRule {
  weekday: number;
  enabled: boolean;
  ranges: AvailabilityRange[];
}

interface AvailabilitySettings {
  timezone: string;
  rules: AvailabilityRule[];
}

function suggestedAvailabilityRange(
  ranges: AvailabilityRange[],
): AvailabilityRange | undefined {
  if (ranges.length >= 10) return undefined;
  const lastEnd = ranges.at(-1)?.end_time ?? "08:00";
  const [hours, minutes] = lastEnd.split(":").map(Number);
  const startMinutes = hours * 60 + minutes + 60;
  if (startMinutes >= 24 * 60 - 1) return undefined;
  const endMinutes = Math.min(startMinutes + 3 * 60, 24 * 60 - 1);
  const format = (value: number) =>
    `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
  return {
    start_time: format(startMinutes),
    end_time: format(endMinutes),
  };
}

interface BookingListItem {
  id: string;
  event_type_id: string;
  event_title: string;
  status: string;
  start_at: string;
  end_at: string;
  invitee_name: string;
  invitee_email: string;
  guest_emails: string[];
  invitee_note: string;
  invitee_timezone: string;
  google_meet_url?: string;
  calendar_sync_error?: string;
  created_at: string;
}

interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  handle: string;
  is_admin: boolean;
  created_at: string;
  google_connected: boolean;
}

const Dashboard: Component = () => {
  const params = new URLSearchParams(window.location.search);
  const initialTab = params.get("tab") as Tab | null;
  const [tab, setTab] = createSignal<Tab>(initialTab || "events");
  const [session, { mutate }] = createResource(async () => {
    try {
      return await api<Session>("/api/me");
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        window.location.replace("/login");
      }
      throw error;
    }
  });

  function selectTab(value: Tab): void {
    setTab(value);
    window.history.replaceState(null, "", `/app?tab=${value}`);
  }

  async function logout(): Promise<void> {
    await api("/api/auth/logout", { method: "POST" });
    window.location.assign("/login");
  }

  return (
    <main class="dashboard-page">
      <Show
        when={session()}
        fallback={
          <div class="page-state">
            <div class="loading-spinner" />
            <p>{session.loading ? "Loading workspace…" : session.error?.message}</p>
          </div>
        }
      >
        {(loaded) => (
          <>
            <aside class="dashboard-sidebar">
              <a class="dashboard-brand" href="/app">
                <Brand
                  name={loaded().user.brand_name}
                  logoUrl={loaded().logo_url}
                  color={loaded().user.brand_color}
                />
              </a>
              <nav aria-label="Workspace">
                <NavButton
                  active={tab() === "events"}
                  label="Event types"
                  icon="◫"
                  onClick={() => selectTab("events")}
                />
                <NavButton
                  active={tab() === "availability"}
                  label="Availability"
                  icon="◷"
                  onClick={() => selectTab("availability")}
                />
                <NavButton
                  active={tab() === "meetings"}
                  label="Meetings"
                  icon="▣"
                  onClick={() => selectTab("meetings")}
                />
                <NavButton
                  active={tab() === "profile"}
                  label="Profile & integrations"
                  icon="⚙"
                  onClick={() => selectTab("profile")}
                />
                <Show when={loaded().user.is_admin}>
                  <NavButton
                    active={tab() === "admin"}
                    label="Administration"
                    icon="◇"
                    onClick={() => selectTab("admin")}
                  />
                </Show>
              </nav>
              <div class="sidebar-user">
                <div class="avatar">
                  {loaded().user.picture_url ? (
                    <img src={loaded().user.picture_url} alt="" />
                  ) : (
                    loaded().user.display_name.slice(0, 1).toUpperCase()
                  )}
                </div>
                <div>
                  <strong>{loaded().user.display_name}</strong>
                  <span>{loaded().user.email}</span>
                </div>
                <button type="button" onClick={logout} aria-label="Sign out">
                  ↗
                </button>
              </div>
            </aside>
            <section class="dashboard-content">
              <Show when={tab() === "events"}>
                <EventTypesPanel user={loaded().user} />
              </Show>
              <Show when={tab() === "availability"}>
                <AvailabilityPanel />
              </Show>
              <Show when={tab() === "meetings"}>
                <MeetingsPanel />
              </Show>
              <Show when={tab() === "profile"}>
                <ProfilePanel
                  session={loaded()}
                  onUpdated={(next) => mutate({ ...loaded(), ...next })}
                />
              </Show>
              <Show when={tab() === "admin" && loaded().user.is_admin}>
                <AdminPanel />
              </Show>
            </section>
          </>
        )}
      </Show>
    </main>
  );
};

const NavButton: Component<{
  active: boolean;
  label: string;
  icon: string;
  onClick: () => void;
}> = (props) => (
  <button
    class="nav-button"
    classList={{ active: props.active }}
    type="button"
    onClick={props.onClick}
  >
    <span>{props.icon}</span>
    {props.label}
  </button>
);

const EventTypesPanel: Component<{ user: User }> = (props) => {
  const [events, { refetch }] = createResource(() =>
    api<EventType[]>("/api/event-types"),
  );
  const [editing, setEditing] = createSignal<EventType | "new">();
  const [error, setError] = createSignal("");
  const [notice, setNotice] = createSignal("");

  async function disable(event: EventType): Promise<void> {
    if (!window.confirm(`Disable “${event.title}”?`)) return;
    await api(`/api/event-types/${event.id}`, { method: "DELETE" });
    await refetch();
  }

  async function copyLink(event: EventType): Promise<void> {
    const link = `${window.location.origin}/${props.user.handle}/${event.slug}`;
    await navigator.clipboard.writeText(link);
    setNotice("Scheduling link copied");
    window.setTimeout(() => setNotice(""), 1800);
  }

  return (
    <div class="dashboard-panel">
      <header class="panel-header">
        <div>
          <p class="eyebrow">Scheduling</p>
          <h1>Event types</h1>
          <p>Create and share one-on-one meeting links.</p>
        </div>
        <button class="primary-button" type="button" onClick={() => setEditing("new")}>
          + New event type
        </button>
      </header>
      <Show when={notice()}>
        <div class="toast" role="status">
          {notice()}
        </div>
      </Show>
      <Show when={events.loading}>
        <div class="panel-loading">Loading event types…</div>
      </Show>
      <div class="event-card-grid">
        <For each={events()}>
          {(event) => (
            <article class="event-card" classList={{ inactive: !event.active }}>
              <div
                class="event-color"
                style={{ background: props.user.brand_color }}
              />
              <div class="event-card-top">
                <span class="meeting-badge">One-on-one</span>
                <span class="status-dot">{event.active ? "Active" : "Disabled"}</span>
              </div>
              <h2>{event.title}</h2>
              <p>
                {event.duration_minutes} min · every {event.slot_increment_minutes} min
              </p>
              <a
                class="public-link"
                href={`/${props.user.handle}/${event.slug}`}
                target="_blank"
              >
                /{props.user.handle}/{event.slug}
              </a>
              <div class="event-actions">
                <button type="button" onClick={() => copyLink(event)}>
                  Copy link
                </button>
                <button type="button" onClick={() => setEditing(event)}>
                  Edit
                </button>
                <Show when={event.active}>
                  <button type="button" class="danger-text" onClick={() => disable(event)}>
                    Disable
                  </button>
                </Show>
              </div>
            </article>
          )}
        </For>
      </div>
      <Show when={!events.loading && events()?.length === 0}>
        <div class="empty-state">
          <h2>No event types yet</h2>
          <p>Create a link people can use to schedule with you.</p>
        </div>
      </Show>
      <Show when={editing()}>
        {(value) => (
          <EventEditor
            event={value() === "new" ? undefined : (value() as EventType)}
            onClose={() => {
              setEditing();
              setError("");
            }}
            onSaved={async () => {
              setEditing();
              setError("");
              await refetch();
            }}
            error={error()}
            setError={setError}
          />
        )}
      </Show>
    </div>
  );
};

const EventEditor: Component<{
  event?: EventType;
  onClose: () => void;
  onSaved: () => Promise<void>;
  error: string;
  setError: (value: string) => void;
}> = (props) => {
  const [title, setTitle] = createSignal(props.event?.title ?? "30 Minute Meeting");
  const [slug, setSlug] = createSignal(props.event?.slug ?? "30min");
  const [description, setDescription] = createSignal(props.event?.description ?? "");
  const [duration, setDuration] = createSignal(props.event?.duration_minutes ?? 30);
  const [increment, setIncrement] = createSignal(
    props.event?.slot_increment_minutes ?? 30,
  );
  const [question, setQuestion] = createSignal(
    props.event?.invitee_question ??
      "Please share anything that will help prepare for our meeting.",
  );
  const [active, setActive] = createSignal(props.event?.active ?? true);
  const [busy, setBusy] = createSignal(false);

  async function submit(eventObject: SubmitEvent): Promise<void> {
    eventObject.preventDefault();
    setBusy(true);
    props.setError("");
    try {
      await api(
        props.event ? `/api/event-types/${props.event.id}` : "/api/event-types",
        {
          method: props.event ? "PUT" : "POST",
          body: JSON.stringify({
            title: title(),
            slug: slug(),
            description: description(),
            duration_minutes: duration(),
            slot_increment_minutes: increment(),
            invitee_question: question(),
            active: active(),
          }),
        },
      );
      await props.onSaved();
    } catch (error) {
      props.setError(error instanceof Error ? error.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="modal-backdrop" onMouseDown={(event) => {
      if (event.target === event.currentTarget) props.onClose();
    }}>
      <section class="editor-modal" role="dialog" aria-modal="true">
        <header>
          <div>
            <p class="eyebrow">One-on-one</p>
            <h2>{props.event ? "Edit event type" : "Create event type"}</h2>
          </div>
          <button type="button" onClick={props.onClose} aria-label="Close">
            ×
          </button>
        </header>
        <form onSubmit={submit}>
          <div class="form-grid">
            <label>
              Event name
              <input required value={title()} onInput={(event) => setTitle(event.currentTarget.value)} />
            </label>
            <label>
              Public link
              <input
                required
                pattern="[a-z0-9][a-z0-9-]+"
                value={slug()}
                onInput={(event) => setSlug(event.currentTarget.value.toLowerCase())}
              />
            </label>
            <label>
              Duration
              <select
                value={duration()}
                onChange={(event) => setDuration(Number(event.currentTarget.value))}
              >
                <For each={[15, 30, 45, 60, 90, 120]}>
                  {(value) => <option value={value}>{value} minutes</option>}
                </For>
              </select>
            </label>
            <label>
              Start time increments
              <select
                value={increment()}
                onChange={(event) => setIncrement(Number(event.currentTarget.value))}
              >
                <For each={[5, 10, 15, 20, 30, 45, 60]}>
                  {(value) => <option value={value}>Every {value} minutes</option>}
                </For>
              </select>
            </label>
          </div>
          <label>
            Description
            <textarea
              rows={3}
              value={description()}
              onInput={(event) => setDescription(event.currentTarget.value)}
            />
          </label>
          <label>
            Invitee question
            <textarea
              required
              rows={2}
              value={question()}
              onInput={(event) => setQuestion(event.currentTarget.value)}
            />
          </label>
          <label class="toggle-row">
            <input
              type="checkbox"
              checked={active()}
              onChange={(event) => setActive(event.currentTarget.checked)}
            />
            <span>Scheduling link is active</span>
          </label>
          <Show when={props.error}>
            <p class="form-error" role="alert">
              {props.error}
            </p>
          </Show>
          <footer>
            <button class="secondary-button" type="button" onClick={props.onClose}>
              Cancel
            </button>
            <button class="primary-button" type="submit" disabled={busy()}>
              {busy() ? "Saving…" : "Save event type"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
};

const AvailabilityPanel: Component = () => {
  const [settings, { mutate }] = createResource(() =>
    api<AvailabilitySettings>("/api/me/availability"),
  );
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");
  const [saved, setSaved] = createSignal(false);
  const [copySourceIndex, setCopySourceIndex] = createSignal<number>();
  const [copyTargetWeekdays, setCopyTargetWeekdays] = createSignal<number[]>([]);
  const dayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const copySourceRule = createMemo(() => {
    const index = copySourceIndex();
    return index === undefined ? undefined : settings()?.rules[index];
  });

  function updateRule(index: number, changes: Partial<AvailabilityRule>): void {
    const current = settings();
    if (!current) return;
    mutate({
      ...current,
      rules: current.rules.map((rule, currentIndex) =>
        currentIndex === index ? { ...rule, ...changes } : rule,
      ),
    });
  }

  function updateRange(
    ruleIndex: number,
    rangeIndex: number,
    changes: Partial<AvailabilityRange>,
  ): void {
    const current = settings();
    if (!current) return;
    mutate({
      ...current,
      rules: current.rules.map((rule, currentRuleIndex) =>
        currentRuleIndex === ruleIndex
          ? {
              ...rule,
              ranges: rule.ranges.map((range, currentRangeIndex) =>
                currentRangeIndex === rangeIndex
                  ? { ...range, ...changes }
                  : range,
              ),
            }
          : rule,
      ),
    });
  }

  function addRange(ruleIndex: number): void {
    const current = settings();
    if (!current) return;
    const range = suggestedAvailabilityRange(current.rules[ruleIndex].ranges);
    if (!range) return;
    updateRule(ruleIndex, {
      ranges: [...current.rules[ruleIndex].ranges, range],
    });
  }

  function removeRange(ruleIndex: number, rangeIndex: number): void {
    const current = settings();
    if (!current || current.rules[ruleIndex].ranges.length <= 1) return;
    updateRule(ruleIndex, {
      ranges: current.rules[ruleIndex].ranges.filter(
        (_, currentRangeIndex) => currentRangeIndex !== rangeIndex,
      ),
    });
  }

  function openCopyHours(ruleIndex: number): void {
    setCopySourceIndex(ruleIndex);
    setCopyTargetWeekdays([]);
  }

  function closeCopyHours(): void {
    setCopySourceIndex(undefined);
    setCopyTargetWeekdays([]);
  }

  function toggleCopyTarget(weekday: number, selected: boolean): void {
    setCopyTargetWeekdays((current) =>
      selected
        ? [...new Set([...current, weekday])]
        : current.filter((value) => value !== weekday),
    );
  }

  function applyCopiedHours(): void {
    const current = settings();
    const source = copySourceRule();
    if (!current || !source || copyTargetWeekdays().length === 0) return;
    const targets = new Set(copyTargetWeekdays());
    mutate({
      ...current,
      rules: current.rules.map((rule) =>
        targets.has(rule.weekday)
          ? {
              ...rule,
              enabled: source.enabled,
              ranges: source.ranges.map((range) => ({ ...range })),
            }
          : rule,
      ),
    });
    closeCopyHours();
  }

  async function save(): Promise<void> {
    const current = settings();
    if (!current) return;
    setBusy(true);
    setError("");
    try {
      const savedSettings = await api<AvailabilitySettings>("/api/me/availability", {
        method: "PUT",
        body: JSON.stringify({ rules: current.rules }),
      });
      mutate(savedSettings);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1800);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="dashboard-panel narrow-panel">
      <header class="panel-header">
        <div>
          <p class="eyebrow">Availability</p>
          <h1>Working hours</h1>
          <p>
            Choose when people can book you in{" "}
            {settings()?.timezone ?? "your timezone"}. Visitors automatically see
            these times converted to their own timezone.
          </p>
        </div>
        <button class="primary-button" type="button" disabled={busy()} onClick={save}>
          {busy() ? "Saving…" : saved() ? "Saved" : "Save changes"}
        </button>
      </header>
      <Show when={settings()} fallback={<div class="panel-loading">Loading availability…</div>}>
        {(loaded) => (
          <div class="availability-editor">
            <Index each={loaded().rules}>
              {(rule, index) => (
                <div class="availability-row">
                  <label class="day-toggle">
                    <input
                      type="checkbox"
                      checked={rule().enabled}
                      onChange={(event) =>
                        updateRule(index, { enabled: event.currentTarget.checked })
                      }
                    />
                    <strong>{dayNames[rule().weekday - 1]}</strong>
                  </label>
                  <Show
                    when={rule().enabled}
                    fallback={<span class="unavailable-label">Unavailable</span>}
                  >
                    <div class="availability-ranges">
                      <Index each={rule().ranges}>
                        {(range, rangeIndex) => (
                          <div class="time-range">
                            <input
                              type="time"
                              aria-label={`${dayNames[rule().weekday - 1]} range ${rangeIndex + 1} start`}
                              value={range().start_time}
                              onInput={(event) =>
                                updateRange(index, rangeIndex, {
                                  start_time: event.currentTarget.value,
                                })
                              }
                            />
                            <span>–</span>
                            <input
                              type="time"
                              aria-label={`${dayNames[rule().weekday - 1]} range ${rangeIndex + 1} end`}
                              value={range().end_time}
                              onInput={(event) =>
                                updateRange(index, rangeIndex, {
                                  end_time: event.currentTarget.value,
                                })
                              }
                            />
                            <button
                              class="remove-time-range"
                              type="button"
                              disabled={rule().ranges.length === 1}
                              aria-label={`Remove ${dayNames[rule().weekday - 1]} range ${rangeIndex + 1}`}
                              onClick={() => removeRange(index, rangeIndex)}
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </Index>
                      <div class="availability-actions">
                        <button
                          class="add-time-range"
                          type="button"
                          disabled={!suggestedAvailabilityRange(rule().ranges)}
                          aria-label={`Add time range for ${dayNames[rule().weekday - 1]}`}
                          onClick={() => addRange(index)}
                        >
                          + Add time range
                        </button>
                        <button
                          class="copy-day-hours"
                          type="button"
                          aria-label={`Copy ${dayNames[rule().weekday - 1]} hours to other days`}
                          onClick={() => openCopyHours(index)}
                        >
                          Copy to days…
                        </button>
                      </div>
                    </div>
                  </Show>
                </div>
              )}
            </Index>
          </div>
        )}
      </Show>
      <Show when={copySourceRule()}>
        {(source) => (
          <div
            class="modal-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeCopyHours();
            }}
          >
            <section
              class="editor-modal copy-hours-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="copy-hours-title"
            >
              <header>
                <div>
                  <p class="eyebrow">Availability</p>
                  <h2 id="copy-hours-title">
                    Copy {dayNames[source().weekday - 1]} hours
                  </h2>
                </div>
                <button type="button" onClick={closeCopyHours} aria-label="Close">
                  ×
                </button>
              </header>
              <p class="copy-hours-description">
                Choose the days that should use the same complete schedule. Their
                existing time ranges will be replaced.
              </p>
              <div class="copy-day-options">
                <For
                  each={(settings()?.rules ?? []).filter(
                    (rule) => rule.weekday !== source().weekday,
                  )}
                >
                  {(rule) => (
                    <label class="copy-day-option">
                      <input
                        type="checkbox"
                        checked={copyTargetWeekdays().includes(rule.weekday)}
                        onChange={(event) =>
                          toggleCopyTarget(
                            rule.weekday,
                            event.currentTarget.checked,
                          )
                        }
                      />
                      <span>{dayNames[rule.weekday - 1]}</span>
                    </label>
                  )}
                </For>
              </div>
              <footer>
                <button
                  class="secondary-button"
                  type="button"
                  onClick={closeCopyHours}
                >
                  Cancel
                </button>
                <button
                  class="primary-button"
                  type="button"
                  disabled={copyTargetWeekdays().length === 0}
                  onClick={applyCopiedHours}
                >
                  Apply to {copyTargetWeekdays().length}{" "}
                  {copyTargetWeekdays().length === 1 ? "day" : "days"}
                </button>
              </footer>
            </section>
          </div>
        )}
      </Show>
      <Show when={error()}>
        <p class="form-error" role="alert">
          {error()}
        </p>
      </Show>
      <div class="settings-note">
        <strong>Booking rules</strong>
        <p>
          New meetings require 4 hours’ notice and can be booked up to 60 days in
          advance. There are no buffers or daily meeting limits.
        </p>
      </div>
    </div>
  );
};

const MeetingsPanel: Component = () => {
  const [bookings, { refetch }] = createResource(() =>
    api<BookingListItem[]>("/api/bookings"),
  );
  const [view, setView] = createSignal<"upcoming" | "past">("upcoming");
  const [error, setError] = createSignal("");
  const visible = createMemo(() => {
    const now = Date.now();
    return (bookings() ?? []).filter((booking) =>
      view() === "upcoming"
        ? new Date(booking.end_at).getTime() >= now && booking.status === "confirmed"
        : new Date(booking.end_at).getTime() < now || booking.status !== "confirmed",
    );
  });

  async function cancel(booking: BookingListItem): Promise<void> {
    if (!window.confirm(`Cancel the meeting with ${booking.invitee_name}?`)) return;
    setError("");
    try {
      await api(`/api/bookings/${booking.id}/cancel`, { method: "POST" });
      await refetch();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not cancel.");
    }
  }

  return (
    <div class="dashboard-panel">
      <header class="panel-header">
        <div>
          <p class="eyebrow">Meetings</p>
          <h1>Scheduled meetings</h1>
          <p>See invitees, notes, Google Meet links, and meeting status.</p>
        </div>
      </header>
      <div class="segmented-control">
        <button classList={{ active: view() === "upcoming" }} onClick={() => setView("upcoming")}>
          Upcoming
        </button>
        <button classList={{ active: view() === "past" }} onClick={() => setView("past")}>
          Past &amp; cancelled
        </button>
      </div>
      <Show when={error()}>
        <p class="form-error" role="alert">{error()}</p>
      </Show>
      <div class="meeting-list">
        <For each={visible()}>
          {(booking) => {
            const start = new Date(booking.start_at);
            return (
              <article class="meeting-row">
                <time datetime={booking.start_at}>
                  <strong>{new Intl.DateTimeFormat("en", { day: "2-digit" }).format(start)}</strong>
                  <span>{new Intl.DateTimeFormat("en", { month: "short" }).format(start)}</span>
                </time>
                <div class="meeting-main">
                  <span class={`booking-status ${booking.status}`}>{booking.status}</span>
                  <h2>{booking.event_title}</h2>
                  <p>
                    {new Intl.DateTimeFormat("en", {
                      weekday: "short",
                      hour: "numeric",
                      minute: "2-digit",
                      timeZone: booking.invitee_timezone,
                    }).format(start)}{" "}
                    · {booking.invitee_name} ({booking.invitee_email})
                  </p>
                  <Show when={booking.invitee_note}>
                    <blockquote>{booking.invitee_note}</blockquote>
                  </Show>
                  <Show when={booking.calendar_sync_error}>
                    <p class="sync-warning">Calendar sync needs attention.</p>
                  </Show>
                </div>
                <div class="meeting-actions">
                  <Show when={booking.google_meet_url && booking.status === "confirmed"}>
                    <a href={booking.google_meet_url} target="_blank">Join Meet</a>
                  </Show>
                  <Show when={booking.status === "confirmed"}>
                    <button type="button" class="danger-text" onClick={() => cancel(booking)}>
                      Cancel
                    </button>
                  </Show>
                </div>
              </article>
            );
          }}
        </For>
      </div>
      <Show when={!bookings.loading && visible().length === 0}>
        <div class="empty-state">
          <h2>No {view()} meetings</h2>
          <p>Bookings will appear here as soon as someone schedules.</p>
        </div>
      </Show>
    </div>
  );
};

const ProfilePanel: Component<{
  session: Session;
  onUpdated: (value: Partial<Session>) => void;
}> = (props) => {
  const [name, setName] = createSignal(props.session.user.display_name);
  const [handle, setHandle] = createSignal(props.session.user.handle);
  const [timezone, setTimezone] = createSignal(props.session.user.timezone);
  const [brandName, setBrandName] = createSignal(props.session.user.brand_name);
  const [brandColor, setBrandColor] = createSignal(props.session.user.brand_color);
  const [logoData, setLogoData] = createSignal<string>();
  const [logoPreview, setLogoPreview] = createSignal(props.session.logo_url);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");
  const [saved, setSaved] = createSignal(false);
  const zones =
    (Intl as typeof Intl & { supportedValuesOf?: (key: "timeZone") => string[] })
      .supportedValuesOf?.("timeZone") ?? ["UTC", "Europe/Nicosia"];

  function chooseLogo(event: Event): void {
    const file = (event.currentTarget as HTMLInputElement).files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setError("Logo must be smaller than 2 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      setLogoData(result);
      setLogoPreview(result);
    };
    reader.readAsDataURL(file);
  }

  async function save(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api<{ user: User; logo_url?: string }>("/api/me", {
        method: "PATCH",
        body: JSON.stringify({
          display_name: name(),
          handle: handle(),
          timezone: timezone(),
          brand_name: brandName(),
          brand_color: brandColor(),
          logo_data_url: logoData(),
        }),
      });
      setLogoPreview(result.logo_url);
      setLogoData(undefined);
      props.onUpdated({ user: result.user, logo_url: result.logo_url });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1800);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="dashboard-panel narrow-panel">
      <header class="panel-header">
        <div>
          <p class="eyebrow">Settings</p>
          <h1>Profile &amp; integrations</h1>
          <p>Control your public identity and calendar connection.</p>
        </div>
      </header>
      <form class="settings-form" onSubmit={save}>
        <section class="settings-section">
          <h2>Branding</h2>
          <div class="logo-editor">
            <div class="logo-preview" style={{ "--preview-color": brandColor() }}>
              <Show when={logoPreview()} fallback={brandName().slice(0, 1).toUpperCase()}>
                {(url) => <img src={url()} alt="Logo preview" />}
              </Show>
            </div>
            <div>
              <label class="upload-button">
                Upload logo
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={chooseLogo}
                />
              </label>
              <p>PNG, JPEG, or WebP. Maximum 2 MB.</p>
            </div>
          </div>
          <div class="form-grid">
            <label>
              Brand name
              <input required value={brandName()} onInput={(event) => setBrandName(event.currentTarget.value)} />
            </label>
            <label>
              Brand color
              <div class="color-input">
                <input type="color" value={brandColor()} onInput={(event) => setBrandColor(event.currentTarget.value)} />
                <input required pattern="#[0-9A-Fa-f]{6}" value={brandColor()} onInput={(event) => setBrandColor(event.currentTarget.value)} />
              </div>
            </label>
          </div>
        </section>
        <section class="settings-section">
          <h2>Public profile</h2>
          <div class="form-grid">
            <label>
              Display name
              <input required value={name()} onInput={(event) => setName(event.currentTarget.value)} />
            </label>
            <label>
              Public handle
              <input
                required
                pattern="[a-z0-9][a-z0-9-]{2,47}"
                value={handle()}
                onInput={(event) => setHandle(event.currentTarget.value.toLowerCase())}
              />
            </label>
          </div>
          <label>
            Timezone
            <select value={timezone()} onChange={(event) => setTimezone(event.currentTarget.value)}>
              <For each={zones}>{(zone) => <option value={zone}>{zone.replaceAll("_", " ")}</option>}</For>
            </select>
          </label>
        </section>
        <section class="settings-section integration-card">
          <div class="google-g">G</div>
          <div>
            <h2>Google Calendar &amp; Meet</h2>
            <p>
              {props.session.google_connected
                ? "Connected. Busy times are hidden and bookings create Google Meet events."
                : "Connect Google to publish live availability and create meetings."}
            </p>
          </div>
          <a
            class={props.session.google_connected ? "secondary-button" : "primary-button"}
            href="/api/integrations/google/start?return_to=/app?tab=profile"
          >
            {props.session.google_connected ? "Reconnect" : "Connect Google"}
          </a>
        </section>
        <Show when={error()}><p class="form-error" role="alert">{error()}</p></Show>
        <div class="settings-save">
          <button class="primary-button" type="submit" disabled={busy()}>
            {busy() ? "Saving…" : saved() ? "Saved" : "Save profile"}
          </button>
        </div>
      </form>
    </div>
  );
};

const AdminPanel: Component = () => {
  const [users] = createResource(() => api<AdminUser[]>("/api/admin/users"));
  return (
    <div class="dashboard-panel">
      <header class="panel-header">
        <div>
          <p class="eyebrow">Administration</p>
          <h1>Users</h1>
          <p>Platform roles are stored in PostgreSQL, not inferred from identity-provider groups.</p>
        </div>
      </header>
      <div class="admin-table-wrap">
        <table class="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Handle</th>
              <th>Google</th>
              <th>Role</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            <For each={users()}>
              {(user) => (
                <tr>
                  <td><strong>{user.display_name}</strong><span>{user.email}</span></td>
                  <td>/{user.handle}</td>
                  <td>{user.google_connected ? "Connected" : "Not connected"}</td>
                  <td>{user.is_admin ? "Admin" : "Host"}</td>
                  <td>{new Intl.DateTimeFormat("en", { dateStyle: "medium" }).format(new Date(user.created_at))}</td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Dashboard;
